import * as fs from 'node:fs';
import * as path from 'node:path';
import { cosmiconfig, cosmiconfigSync } from 'cosmiconfig';
import { z } from 'zod';
import { defaultConfig } from './defaults.js';
import { type Configuration, configSchema } from './schema.js';

/**
 * Configuration loader with cosmiconfig integration
 * Searches for .mdexportrc files across multiple formats
 * Implements configuration merging strategy: CLI args > file > defaults
 */
export class ConfigLoader {
  private readonly moduleName = 'mdexport';
  private readonly namespace = 'ConfigLoader';
  private explorer: ReturnType<typeof cosmiconfig>;
  private explorerSync: ReturnType<typeof cosmiconfigSync>;

  // Search locations for config files (in priority order)
  private readonly searchPlaces = [
    '.mdexportrc',
    '.mdexportrc.json',
    '.mdexportrc.yaml',
    '.mdexportrc.yml',
    '.mdexportrc.js',
    '.mdexportrc.cjs',
    'mdexport.config.js',
    'mdexport.config.cjs',
    'package.json', // Look for "mdexport" key in package.json
  ];

  constructor() {
    // Initialize cosmiconfig explorer for async operations
    // Note: stopDir is not set here to avoid calling process.cwd() during construction
    // which could fail if CWD doesn't exist (e.g., during tests)
    this.explorer = cosmiconfig(this.moduleName, {
      searchPlaces: this.searchPlaces,
    });

    // Initialize sync explorer for synchronous operations
    this.explorerSync = cosmiconfigSync(this.moduleName, {
      searchPlaces: this.searchPlaces,
    });
  }

  /**
   * Load configuration asynchronously
   * @param configPath - Optional explicit config file path
   * @param cliOverrides - Optional CLI argument overrides
   * @returns Merged and validated configuration
   */
  async load(configPath?: string, cliOverrides?: Partial<Configuration>): Promise<Configuration> {
    try {
      let loadedConfig: Partial<Configuration> = {};

      // 1. Load from file (explicit path or search)
      if (configPath) {
        this.log('info', `Loading configuration from: ${configPath}`);
        try {
          const result = await this.explorer.load(configPath);
          if (result) {
            loadedConfig = result.config;
            this.log('debug', `Loaded config from: ${result.filepath}`);
          } else {
            throw new Error(`Configuration file not found: ${configPath}`);
          }
        } catch (_error) {
          // Convert file system errors to our expected error format
          throw new Error(`Configuration file not found: ${configPath}`);
        }
      } else {
        // Search for config file starting from current directory
        const result = await this.explorer.search();
        if (result) {
          loadedConfig = result.config;
          this.log('info', `Found configuration at: ${result.filepath}`);
        } else {
          this.log('info', 'No configuration file found, using defaults');
        }
      }

      // 2. Merge configurations: CLI > file > defaults
      const mergedConfig = this.mergeConfigurations(
        defaultConfig,
        loadedConfig,
        cliOverrides || {}
      );

      // 3. Validate the merged configuration
      const validatedConfig = this.validate(mergedConfig);

      this.log('debug', 'Configuration loaded and validated successfully');
      return validatedConfig;
    } catch (error) {
      this.log('error', 'Failed to load configuration:', error);
      throw error;
    }
  }

  /**
   * Load configuration synchronously
   * @param configPath - Optional explicit config file path
   * @param cliOverrides - Optional CLI argument overrides
   * @returns Merged and validated configuration
   */
  loadSync(configPath?: string, cliOverrides?: Partial<Configuration>): Configuration {
    try {
      let loadedConfig: Partial<Configuration> = {};

      // 1. Load from file (explicit path or search)
      if (configPath) {
        this.log('info', `Loading configuration from: ${configPath}`);
        try {
          const result = this.explorerSync.load(configPath);
          if (result) {
            loadedConfig = result.config;
            this.log('debug', `Loaded config from: ${result.filepath}`);
          } else {
            throw new Error(`Configuration file not found: ${configPath}`);
          }
        } catch (_error) {
          // Convert file system errors to our expected error format
          throw new Error(`Configuration file not found: ${configPath}`);
        }
      } else {
        // Search for config file starting from current directory
        const result = this.explorerSync.search();
        if (result) {
          loadedConfig = result.config;
          this.log('info', `Found configuration at: ${result.filepath}`);
        } else {
          this.log('info', 'No configuration file found, using defaults');
        }
      }

      // 2. Merge configurations: CLI > file > defaults
      const mergedConfig = this.mergeConfigurations(
        defaultConfig,
        loadedConfig,
        cliOverrides || {}
      );

      // 3. Validate the merged configuration
      const validatedConfig = this.validate(mergedConfig);

      this.log('debug', 'Configuration loaded and validated successfully');
      return validatedConfig;
    } catch (error) {
      this.log('error', 'Failed to load configuration:', error);
      throw error;
    }
  }

  /**
   * Search for configuration file without loading
   * @param searchFrom - Directory to start search from
   * @returns Path to configuration file or null
   */
  async search(searchFrom?: string): Promise<string | null> {
    try {
      const result = searchFrom
        ? await this.explorer.search(searchFrom)
        : await this.explorer.search();

      return result ? result.filepath : null;
    } catch (error) {
      this.log('error', 'Error searching for configuration:', error);
      return null;
    }
  }

  /**
   * Search for configuration file synchronously
   * @param searchFrom - Directory to start search from
   * @returns Path to configuration file or null
   */
  searchSync(searchFrom?: string): string | null {
    try {
      const result = searchFrom ? this.explorerSync.search(searchFrom) : this.explorerSync.search();

      return result ? result.filepath : null;
    } catch (error) {
      this.log('error', 'Error searching for configuration:', error);
      return null;
    }
  }

  /**
   * Validate configuration against schema
   * @param config - Configuration to validate
   * @returns Validated configuration
   * @throws {z.ZodError} If validation fails
   */
  validate(config: unknown): Configuration {
    try {
      return configSchema.parse(config);
    } catch (error) {
      if (error instanceof z.ZodError) {
        this.log('error', 'Configuration validation failed:');
        for (const issue of error.issues) {
          this.log('error', `  - ${issue.path.join('.')}: ${issue.message}`);
        }
      }
      throw new Error('Configuration validation failed. See errors above.');
    }
  }

  /**
   * Deep merge configurations with priority: high > medium > low
   * Arrays are replaced (not concatenated), objects are merged recursively
   * @param low - Lowest priority configuration (defaults)
   * @param medium - Medium priority configuration (file)
   * @param high - Highest priority configuration (CLI)
   * @returns Merged configuration
   */
  private mergeConfigurations(
    low: Partial<Configuration>,
    medium: Partial<Configuration>,
    high: Partial<Configuration>
  ): Configuration {
    return this.deepMerge(this.deepMerge(low, medium), high) as Configuration;
  }

  /**
   * Deep merge two objects recursively
   * @param target - Target object (lower priority)
   * @param source - Source object (higher priority)
   * @returns Merged object
   */
  private deepMerge(target: Record<string, any>, source: Record<string, any>): Record<string, any> {
    const result = { ...target };

    for (const key in source) {
      if (Object.hasOwn(source, key)) {
        const sourceValue = source[key];
        const targetValue = result[key];

        // If source value is undefined or null, skip it
        if (sourceValue === undefined || sourceValue === null) {
          continue;
        }

        // If both are objects (but not arrays), merge recursively
        if (this.isPlainObject(targetValue) && this.isPlainObject(sourceValue)) {
          result[key] = this.deepMerge(targetValue, sourceValue);
        } else {
          // Otherwise, replace with source value
          // This includes arrays (replace, don't concatenate)
          result[key] = sourceValue;
        }
      }
    }

    return result;
  }

  /**
   * Check if value is a plain object (not array, null, etc.)
   * @param value - Value to check
   * @returns True if plain object
   */
  private isPlainObject(value: unknown): value is Record<string, any> {
    return (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      Object.prototype.toString.call(value) === '[object Object]'
    );
  }

  /**
   * Generate default configuration file
   * @param format - File format ('json' | 'yaml' | 'js')
   * @param outputPath - Output file path
   * @returns Path to generated file
   */
  async generateConfigFile(
    format: 'json' | 'yaml' | 'js' = 'json',
    outputPath?: string
  ): Promise<string> {
    const fileName = outputPath || `.mdexportrc.${format}`;
    const fullPath = path.resolve(process.cwd(), fileName);

    try {
      let content: string;

      switch (format) {
        case 'json':
          content = JSON.stringify(defaultConfig, null, 2);
          break;

        case 'yaml':
          // Convert to YAML format
          content = this.convertToYaml(defaultConfig);
          break;

        case 'js':
          content = `module.exports = ${JSON.stringify(defaultConfig, null, 2)};\n`;
          break;

        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      await fs.promises.writeFile(fullPath, content, 'utf-8');
      this.log('info', `Configuration file generated: ${fullPath}`);

      return fullPath;
    } catch (error) {
      this.log('error', 'Failed to generate configuration file:', error);
      throw error;
    }
  }

  /**
   * Simple YAML converter (basic implementation)
   * For production, consider using a proper YAML library
   * @param obj - Object to convert
   * @param indent - Current indentation level
   * @returns YAML string
   */
  private convertToYaml(obj: any, indent = 0): string {
    const spaces = '  '.repeat(indent);
    let yaml = '';

    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) {
        yaml += `${spaces}${key}: null\n`;
      } else if (Array.isArray(value)) {
        yaml += `${spaces}${key}:\n`;
        if (value.length === 0) {
          yaml += `${spaces}  []\n`;
        } else {
          for (const item of value) {
            if (typeof item === 'object' && item !== null) {
              yaml += `${spaces}  -\n`;
              yaml += this.convertToYaml(item, indent + 2);
            } else {
              yaml += `${spaces}  - ${this.yamlValue(item)}\n`;
            }
          }
        }
      } else if (typeof value === 'object') {
        yaml += `${spaces}${key}:\n`;
        yaml += this.convertToYaml(value, indent + 1);
      } else {
        yaml += `${spaces}${key}: ${this.yamlValue(value)}\n`;
      }
    }

    return yaml;
  }

  /**
   * Format value for YAML output
   * @param value - Value to format
   * @returns Formatted string
   */
  private yamlValue(value: any): string {
    if (typeof value === 'string') {
      // Quote strings that contain special characters or spaces
      if (/[:#[\]{}|>@`!%&*]|\s/.test(value)) {
        return `"${value.replace(/"/g, '\\"')}"`;
      }
      return value;
    }
    return String(value);
  }

  /**
   * Clear the configuration cache
   * Useful for testing or reloading configuration
   */
  clearCache(): void {
    this.explorer.clearCaches();
    this.explorerSync.clearCaches();
    this.log('debug', 'Configuration cache cleared');
  }

  /**
   * Simple logging method
   * @param level - Log level
   * @param message - Log message
   * @param error - Optional error object
   */
  private log(level: 'error' | 'warn' | 'info' | 'debug', message: string, error?: unknown): void {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${this.namespace}] ${level.toUpperCase()}:`;

    if (level === 'error' && error) {
      if (error instanceof Error) {
        console.error(prefix, message, error.message);
        if (process.env.DEBUG) {
          console.error(error.stack);
        }
      } else {
        console.error(prefix, message, error);
      }
    } else if (level === 'warn') {
      console.warn(prefix, message);
    } else if (level === 'info') {
      console.log(prefix, message);
    } else if (level === 'debug' && process.env.DEBUG) {
      console.debug(prefix, message);
    }
  }
}
